/**
 * OHLC transforms and helpers for TradingView-style chart types (lightweight-charts).
 *
 * Range bars: uses OHLC aggregates per source bar only; intra-bar path is approximated.
 * Point & Figure: brick stream from close (Renko-style) with independent box/reversal params;
 * classic X/O column semantics are not replicated — see pointFigureOhlc.
 */

import { calculateATR } from "./indicatorCalculations";

export const PRICE_SERIES_STYLES = {
  CANDLES: "candles",
  HOLLOW: "hollow",
  BARS: "bars",
  LINE: "line",
  AREA: "area",
  BASELINE: "baseline",
  HEIKIN_ASHI: "heikin_ashi",
  RENKO: "renko",
  LINE_BREAK: "line_break",
  RANGE: "range",
  KAGI: "kagi",
  POINT_FIGURE: "point_figure",
};

/** Labels for UI */
export const PRICE_SERIES_OPTIONS = [
  { value: PRICE_SERIES_STYLES.CANDLES, label: "Candles" },
  { value: PRICE_SERIES_STYLES.HOLLOW, label: "Hollow candles" },
  { value: PRICE_SERIES_STYLES.BARS, label: "Bars" },
  { value: PRICE_SERIES_STYLES.LINE, label: "Line" },
  { value: PRICE_SERIES_STYLES.AREA, label: "Area" },
  { value: PRICE_SERIES_STYLES.BASELINE, label: "Baseline" },
  { value: PRICE_SERIES_STYLES.HEIKIN_ASHI, label: "Heikin Ashi" },
  { value: PRICE_SERIES_STYLES.RENKO, label: "Renko" },
  { value: PRICE_SERIES_STYLES.LINE_BREAK, label: "Line Break" },
  { value: PRICE_SERIES_STYLES.RANGE, label: "Range" },
  { value: PRICE_SERIES_STYLES.KAGI, label: "Kagi" },
  { value: PRICE_SERIES_STYLES.POINT_FIGURE, label: "Point & Figure" },
];

/** Single-value series price source (line / area / baseline / renko brick input). */
export const LINE_PRICE_SOURCE = {
  CLOSE: "close",
  OPEN: "open",
  HIGH: "high",
  LOW: "low",
  HL2: "hl2",
  HLC3: "hlc3",
  OHLC4: "ohlc4",
  MEDIAN: "median",
};

export const LINE_PRICE_SOURCE_OPTIONS = [
  { value: LINE_PRICE_SOURCE.CLOSE, label: "Close" },
  { value: LINE_PRICE_SOURCE.OPEN, label: "Open" },
  { value: LINE_PRICE_SOURCE.HIGH, label: "High" },
  { value: LINE_PRICE_SOURCE.LOW, label: "Low" },
  { value: LINE_PRICE_SOURCE.HL2, label: "HL/2" },
  { value: LINE_PRICE_SOURCE.HLC3, label: "HLC/3" },
  { value: LINE_PRICE_SOURCE.OHLC4, label: "OHLC/4" },
  { value: LINE_PRICE_SOURCE.MEDIAN, label: "Median (O,H,L,C)" },
];

export const RENKO_SOURCE = LINE_PRICE_SOURCE;

export const RENKO_BRICK_MODE = {
  FIXED: "fixed",
  ATR: "atr",
};

export const RANGE_SOURCE = LINE_PRICE_SOURCE;

export const KAGI_MODE = {
  ABSOLUTE: "absolute",
  PERCENT: "percent",
};

/** Maps to lightweight-charts `LineType` for Line / Area. */
export const LINE_CHART_LINE_TYPE = {
  SIMPLE: "simple",
  STEP: "step",
  CURVED: "curved",
};

export const LINE_CHART_LINE_TYPE_OPTIONS = [
  { value: LINE_CHART_LINE_TYPE.SIMPLE, label: "Simple" },
  { value: LINE_CHART_LINE_TYPE.STEP, label: "Step" },
  { value: LINE_CHART_LINE_TYPE.CURVED, label: "Curved" },
];

const median = (values) => {
  if (!values.length) {
    return 0;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const medianOfFour = (o, h, l, c) => median([o, h, l, c]);

/**
 * Price at bar for line/renko/range direction (TV-style).
 */
export function barPriceFromSource(c, source = LINE_PRICE_SOURCE.CLOSE) {
  if (!c) {
    return 0;
  }
  switch (source) {
    case LINE_PRICE_SOURCE.OPEN:
      return c.open;
    case LINE_PRICE_SOURCE.HIGH:
      return c.high;
    case LINE_PRICE_SOURCE.LOW:
      return c.low;
    case LINE_PRICE_SOURCE.HL2:
      return (c.high + c.low) / 2;
    case LINE_PRICE_SOURCE.HLC3:
      return (c.high + c.low + c.close) / 3;
    case LINE_PRICE_SOURCE.OHLC4:
      return (c.open + c.high + c.low + c.close) / 4;
    case LINE_PRICE_SOURCE.MEDIAN:
      return medianOfFour(c.open, c.high, c.low, c.close);
    case LINE_PRICE_SOURCE.CLOSE:
    default:
      return c.close;
  }
}

/**
 * Line / area / baseline single-value series from OHLC bars.
 */
export function lineSeriesFromCandles(candles, source = LINE_PRICE_SOURCE.CLOSE) {
  if (!candles.length) {
    return [];
  }
  return candles.map((c) => ({
    time: c.time,
    value: barPriceFromSource(c, source),
  }));
}

/**
 * Typical Renko brick size from recent ranges (fallback if user does not set).
 */
export function defaultRenkoBrickSize(candles) {
  if (!candles.length) {
    return 1;
  }
  const ranges = candles.slice(-200).map((c) => Math.max(1e-9, c.high - c.low));
  const m = median(ranges);
  const price = Math.max(Math.abs(candles[candles.length - 1]?.close ?? 1), 1e-12);
  const pct = Math.max((m / price) * 0.5, 1e-5);
  return Math.max(price * pct, price * 1e-8);
}

/**
 * Last ATR value as a brick size candidate (Renko ATR mode).
 */
export function atrBrickSize(candles, period = 14) {
  const atrData = calculateATR(candles, period);
  if (!atrData.length) {
    return null;
  }
  const last = atrData[atrData.length - 1]?.value;
  return Number.isFinite(last) && last > 0 ? last : null;
}

export function defaultRangeSize(candles) {
  if (!candles.length) {
    return 1;
  }
  const ranges = candles
    .slice(-200)
    .map((c) => Math.max(1e-9, Math.abs(c.high - c.low)));
  return Math.max(median(ranges), 1e-9);
}

export function heikinAshi(candles) {
  if (!candles.length) {
    return [];
  }

  const first = candles[0];
  let haOpen = (first.open + first.close) / 2;
  let haClose = (first.open + first.high + first.low + first.close) / 4;

  const out = [
    {
      time: first.time,
      open: haOpen,
      high: Math.max(first.high, haOpen, haClose),
      low: Math.min(first.low, haOpen, haClose),
      close: haClose,
    },
  ];

  for (let i = 1; i < candles.length; i += 1) {
    const c = candles[i];
    const prev = out[i - 1];
    haClose = (c.open + c.high + c.low + c.close) / 4;
    haOpen = (prev.open + prev.close) / 2;
    const haHigh = Math.max(c.high, haOpen, haClose);
    const haLow = Math.min(c.low, haOpen, haClose);
    out.push({
      time: c.time,
      open: haOpen,
      high: haHigh,
      low: haLow,
      close: haClose,
    });
  }

  return out;
}

/** Upper bound on Renko bricks emitted (avoids UI freeze when brick size is too small). */
const MAX_RENKO_BRICKS = 50000;

/**
 * Raise brick size if needed so full-range movement cannot explode past MAX_RENKO_BRICKS.
 */
export function clampRenkoBrickSize(candles, renkoSource, brickSize) {
  if (!candles.length || !(brickSize > 0)) {
    return brickSize;
  }
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < candles.length; i += 1) {
    const v = barPriceFromSource(candles[i], renkoSource);
    if (Number.isFinite(v)) {
      lo = Math.min(lo, v);
      hi = Math.max(hi, v);
    }
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    return brickSize;
  }
  const span = Math.max(hi - lo, Number.EPSILON);
  const minForSpan = span / MAX_RENKO_BRICKS;
  const ref = Math.max(Math.abs(hi), Math.abs(lo), 1);
  const absoluteFloor = Math.max(ref * 1e-10, 1e-15);
  return Math.max(brickSize, minForSpan, absoluteFloor);
}

/**
 * Close-driven Renko bricks (OHLC per brick; time = bar that printed the brick).
 * Uses a derived price series for brick boundaries (default: close).
 * Batched stepping + cap prevents pathological runtimes when brick size is tiny.
 */
export function renkoOhlc(candles, brickSize, renkoSource = LINE_PRICE_SOURCE.CLOSE) {
  if (!candles.length || !(brickSize > 0)) {
    return candles;
  }

  const brick = clampRenkoBrickSize(candles, renkoSource, brickSize);
  const out = [];
  let last = barPriceFromSource(candles[0], renkoSource);
  let steps = 0;

  for (let i = 1; i < candles.length && steps < MAX_RENKO_BRICKS; i += 1) {
    const close = barPriceFromSource(candles[i], renkoSource);
    const time = candles[i].time;

    if (close - last >= brick) {
      let n = Math.floor((close - last) / brick);
      n = Math.min(n, MAX_RENKO_BRICKS - steps);
      for (let k = 0; k < n; k += 1) {
        const o = last;
        last += brick;
        out.push({
          time,
          open: o,
          high: Math.max(o, last),
          low: Math.min(o, last),
          close: last,
        });
        steps += 1;
      }
    }

    if (steps >= MAX_RENKO_BRICKS) {
      break;
    }

    if (last - close >= brick) {
      let n = Math.floor((last - close) / brick);
      n = Math.min(n, MAX_RENKO_BRICKS - steps);
      for (let k = 0; k < n; k += 1) {
        const o = last;
        last -= brick;
        out.push({
          time,
          open: o,
          high: Math.max(o, last),
          low: Math.min(o, last),
          close: last,
        });
        steps += 1;
      }
    }
  }

  return out.length ? out : [{ ...candles[0] }];
}

/**
 * Range bars: cumulative high/low since the synthetic open; each printed bar collapses the
 * forming window to that close (OHLC aggregates only; not tick-accurate).
 */
export function rangeBarsOhlc(
  candles,
  rangeSize,
  rangeSource = LINE_PRICE_SOURCE.CLOSE
) {
  if (!candles.length || !(rangeSize > 0)) {
    return candles;
  }

  const out = [];
  let open = candles[0].close;
  let formingHigh = open;
  let formingLow = open;

  for (let i = 1; i < candles.length; i += 1) {
    const c = candles[i];
    formingHigh = Math.max(formingHigh, c.high);
    formingLow = Math.min(formingLow, c.low);

    while (formingHigh - formingLow >= rangeSize) {
      const ref = barPriceFromSource(c, rangeSource);
      const bullish = ref >= open;
      const close = bullish ? open + rangeSize : open - rangeSize;
      out.push({
        time: c.time,
        open,
        high: Math.max(open, close),
        low: Math.min(open, close),
        close,
      });
      open = close;
      formingHigh = close;
      formingLow = close;
    }
  }

  return out.length ? out : [{ ...candles[0] }];
}

export function lineBreakOhlc(candles, lineCount = 3) {
  if (!candles.length) {
    return [];
  }

  const out = [{ ...candles[0] }];
  const n = Math.max(1, Math.floor(lineCount));

  for (let i = 1; i < candles.length; i += 1) {
    const c = candles[i];
    const recent = out.slice(-n);
    const highestClose = Math.max(...recent.map((x) => x.close));
    const lowestClose = Math.min(...recent.map((x) => x.close));
    const prevClose = out[out.length - 1].close;

    if (c.close > highestClose || c.close < lowestClose) {
      out.push({
        time: c.time,
        open: prevClose,
        high: Math.max(prevClose, c.close),
        low: Math.min(prevClose, c.close),
        close: c.close,
      });
    }
  }

  return out.length ? out : [{ ...candles[0] }];
}

/**
 * Kagi line: reversal as absolute price or percent of pivot.
 */
export function kagiLineData(candles, reversalAmount, options = {}) {
  const { mode = KAGI_MODE.ABSOLUTE } = options;

  if (!candles.length || !(reversalAmount > 0)) {
    return closeLineData(candles);
  }

  const out = [{ time: candles[0].time, value: candles[0].close }];
  let direction = 0;
  let pivot = candles[0].close;

  const reversalThreshold = () =>
    mode === KAGI_MODE.PERCENT
      ? Math.max(Math.abs(pivot), 1e-9) * (reversalAmount / 100)
      : reversalAmount;

  for (let i = 1; i < candles.length; i += 1) {
    const c = candles[i];
    const delta = c.close - pivot;
    const thr = reversalThreshold();

    if (direction === 0) {
      if (Math.abs(delta) >= thr) {
        direction = delta > 0 ? 1 : -1;
        pivot = c.close;
        out.push({ time: c.time, value: pivot });
      }
      continue;
    }

    const sameDirection = direction > 0 ? c.close >= pivot : c.close <= pivot;
    const reversedEnough =
      direction > 0 ? pivot - c.close >= thr : c.close - pivot >= thr;

    if (sameDirection || reversedEnough) {
      if (reversedEnough) {
        direction *= -1;
      }
      pivot = c.close;
      out.push({ time: c.time, value: pivot });
    }
  }

  return out;
}

export function closeLineData(candles) {
  return candles.map((c) => ({ time: c.time, value: c.close }));
}

/**
 * Ratio candles: full OHLC of dataset 1 divided pairwise by dataset 2 (o1/o2, h1/h2,
 * l1/l2, c1/c2), inner-joined on exact bar time. Dividing high/low independently doesn't
 * guarantee the usual high >= open/close >= low ordering, so the result is re-clamped
 * into a valid candle shape. Bars whose time has no exact match in the other dataset are
 * dropped rather than fuzzy-matched, since nearest-time matching could silently pair bars
 * from different sessions.
 */
export function computeRatioCandles(candles1, candles2) {
  if (!candles1.length || !candles2.length) {
    return [];
  }
  const byTime2 = new Map(candles2.map((c) => [c.time, c]));
  const out = [];
  for (const c1 of candles1) {
    const c2 = byTime2.get(c1.time);
    if (!c2 || !(c2.open > 0 && c2.high > 0 && c2.low > 0 && c2.close > 0)) continue;
    const o = c1.open / c2.open;
    const h = c1.high / c2.high;
    const l = c1.low / c2.low;
    const c = c1.close / c2.close;
    out.push({
      time: c1.time,
      open: o,
      high: Math.max(o, h, l, c),
      low: Math.min(o, h, l, c),
      close: c,
    });
  }
  return out;
}

/**
 * Point & Figure (approximate): Renko-style OHLC brick stream from close; independent box size.
 * `reversal` is kept for settings parity with TradingView; classic X/O column reversal is not modeled here.
 */
export function pointFigureOhlc(candles, boxSize, _reversal = 3) {
  void _reversal;
  return renkoOhlc(candles, boxSize, LINE_PRICE_SOURCE.CLOSE);
}

function normalizeLinePriceSource(src) {
  const values = Object.values(LINE_PRICE_SOURCE);
  return values.includes(src) ? src : LINE_PRICE_SOURCE.CLOSE;
}

function normalizeLineChartLineType(t) {
  const values = Object.values(LINE_CHART_LINE_TYPE);
  return values.includes(t) ? t : LINE_CHART_LINE_TYPE.SIMPLE;
}

function buildPriceChartPayload(style, candles, options = {}) {
  const {
    renkoBrickSize,
    renkoBrickMode = RENKO_BRICK_MODE.FIXED,
    renkoAtrPeriod = 14,
    renkoSource = LINE_PRICE_SOURCE.CLOSE,
    rangeSize,
    rangeSource = LINE_PRICE_SOURCE.CLOSE,
    lineBreakLines = 3,
    kagiReversal,
    kagiMode = KAGI_MODE.ABSOLUTE,
    pnfBoxSize,
    pnfReversal = 3,
    linePriceSource = LINE_PRICE_SOURCE.CLOSE,
    lineChartLineType: lineChartLineTypeRaw = LINE_CHART_LINE_TYPE.SIMPLE,
    baselinePriceInput,
  } = options;

  const lineChartLineType = normalizeLineChartLineType(lineChartLineTypeRaw);
  const lineSrc = normalizeLinePriceSource(linePriceSource);
  const renkoSrc = normalizeLinePriceSource(renkoSource);
  const rangeSrc = normalizeLinePriceSource(rangeSource);

  switch (style) {
    case PRICE_SERIES_STYLES.HEIKIN_ASHI:
      return {
        kind: "ohlc",
        data: heikinAshi(candles),
        hollow: false,
      };
    case PRICE_SERIES_STYLES.RENKO: {
      let brick =
        Number.isFinite(renkoBrickSize) && renkoBrickSize > 0
          ? renkoBrickSize
          : defaultRenkoBrickSize(candles);
      if (renkoBrickMode === RENKO_BRICK_MODE.ATR) {
        const atr = atrBrickSize(candles, renkoAtrPeriod);
        if (atr !== null) {
          brick = atr;
        }
      }
      return {
        kind: "ohlc",
        data: renkoOhlc(candles, brick, renkoSrc),
        hollow: false,
      };
    }
    case PRICE_SERIES_STYLES.LINE_BREAK:
      return {
        kind: "ohlc",
        data: lineBreakOhlc(candles, lineBreakLines),
        hollow: false,
      };
    case PRICE_SERIES_STYLES.RANGE: {
      const range =
        Number.isFinite(rangeSize) && rangeSize > 0
          ? rangeSize
          : defaultRangeSize(candles);
      return {
        kind: "ohlc",
        data: rangeBarsOhlc(candles, range, rangeSrc),
        hollow: false,
      };
    }
    case PRICE_SERIES_STYLES.KAGI: {
      const reversal =
        Number.isFinite(kagiReversal) && kagiReversal > 0
          ? kagiReversal
          : defaultRangeSize(candles);
      return {
        kind: "line",
        data: kagiLineData(candles, reversal, { mode: kagiMode }),
      };
    }
    case PRICE_SERIES_STYLES.POINT_FIGURE: {
      const box =
        Number.isFinite(pnfBoxSize) && pnfBoxSize > 0
          ? pnfBoxSize
          : defaultRangeSize(candles);
      const rev =
        Number.isFinite(pnfReversal) && pnfReversal > 0
          ? pnfReversal
          : 3;
      return {
        kind: "ohlc",
        data: pointFigureOhlc(candles, box, rev),
        hollow: true,
      };
    }
    case PRICE_SERIES_STYLES.LINE:
      return {
        kind: "line",
        data: lineSeriesFromCandles(candles, lineSrc),
        lineChartLineType,
      };
    case PRICE_SERIES_STYLES.AREA:
      return {
        kind: "area",
        data: lineSeriesFromCandles(candles, lineSrc),
        lineChartLineType,
      };
    case PRICE_SERIES_STYLES.BASELINE: {
      const line = lineSeriesFromCandles(candles, lineSrc);
      const userBase = parseFloat(String(baselinePriceInput ?? "").trim(), 10);
      const base = Number.isFinite(userBase)
        ? userBase
        : line.length && Number.isFinite(line[0].value)
          ? line[0].value
          : 0;
      return { kind: "baseline", data: line, baselinePrice: base };
    }
    case PRICE_SERIES_STYLES.BARS:
      return { kind: "ohlc", data: candles, hollow: false, bar: true };
    case PRICE_SERIES_STYLES.HOLLOW:
      return {
        kind: "ohlc",
        data: candles,
        hollow: true,
      };
    case PRICE_SERIES_STYLES.CANDLES:
    default:
      return { kind: "ohlc", data: candles, hollow: false };
  }
}

export function getPriceChartPayload(style, candles, options = {}) {
  return buildPriceChartPayload(style, candles, options);
}
